/*
 * Copyright (c) Arbeidskracht Groep B.V. - all rights reserved
 */

import {
	accountRecoverySceneActivated,
	accountRecoverySceneEnterPasswordValidationException,
	accountRecoverySceneEnterPhoneVerificationCodeUnexpectedError,
	accountRecoverySceneEnterPhoneVerificationCodeValidationException,
	accountRecoveryScenePasswordInputChanged,
	accountRecoveryScenePasswordStrengthEvaluated,
	accountRecoveryScenePasswordSubmitted,
	accountRecoveryScenePhoneVerificationCodeInputChanged,
	accountRecoveryScenePhoneVerificationCodeSubmitted,
	accountRecoverySceneRequestRestartPhoneVerification,
	accountRecoverySceneRequestRetryPhoneVerification,
	accountRecoverySceneRestartPhoneVerificationUnexpectedError,
	accountRecoverySceneRetryPhoneVerificationUnexpectedError,
	accountRecoverySceneStateChanged,
	accountRecoverySceneStateRetrieved,
	ActionTypes
} from './actions';
import {getType} from 'typesafe-actions';
import {MessageType} from '../../model/messageType';
import {DisplayType} from './displayType';
import {ConstraintViolationDTO} from '../../model/validation';
import {combineReducers} from 'redux';

export interface EnterPasswordState {
	email: string;
	minimumStrength: number;
	strength: number;
	submitDisabled: boolean;
	violations: { [key: string]: ConstraintViolationDTO[] };
}

const enterPasswordInitialState: EnterPasswordState = {
	email: '',
	minimumStrength: 0,
	strength: 0,
	submitDisabled: false,
	violations: {}
};

export interface IndexState {
	displayType: DisplayType;
	message: string;
	messageType: MessageType;
	phoneVerificationWasStopped: boolean;
}

const indexInitialState: IndexState = {
	displayType: DisplayType.DISPLAY_TYPE_NONE,
	message: '',
	messageType: MessageType.INFO,
	phoneVerificationWasStopped: false
};

export interface PhoneVerificationCodeState {
	maximumDurationInSeconds: number | null;
	submitDisabled: boolean;
	verificationStopped: boolean;
	violations: { [key: string]: ConstraintViolationDTO[] };
}

const phoneVerificationCodeInitialState: PhoneVerificationCodeState = {
	maximumDurationInSeconds: null,
	submitDisabled: false,
	verificationStopped: false,
	violations: {}
};

export interface RetryPhoneVerificationStartState {
	buttonText: string;
	submitDisabled: boolean;
}

const retryPhoneVerificationStartInitialState: RetryPhoneVerificationStartState = {
	buttonText: 'Probeer opnieuw',
	submitDisabled: false
};

export interface AccountRecoveryState {
	enterPassword: EnterPasswordState;
	index: IndexState;
	phoneVerificationCode: PhoneVerificationCodeState;
	retryPhoneVerificationStart: RetryPhoneVerificationStartState;
}

const enterPasswordReducer = (
	state: EnterPasswordState = enterPasswordInitialState,
	action: ActionTypes
): EnterPasswordState => {
	const violations = {...state.violations};
	let strength = state.strength;
	let submitDisabled = state.submitDisabled;
	switch (action.type) {
		case getType(accountRecoverySceneActivated):
			return enterPasswordInitialState;
		case getType(accountRecoverySceneStateRetrieved):
		case getType(accountRecoverySceneStateChanged):
			if (action.payload.state.insufficientEvaluatedPasswordStrength) {
				violations.password = [{message: 'Het nieuwe wachtwoord is niet sterk (veilig) genoeg'}];
				strength = action.payload.state.insufficientEvaluatedPasswordStrength.strength;
				submitDisabled = false;
			}
			if (action.payload.state.passwordNotChangedWithSamePassword) {
				violations.password = [{message: 'Het nieuwe wachtwoord mag niet gelijk zijn aan het huidige wachtwoord'}];
				submitDisabled = false;
			}
			return {
				...state,
				email: action.payload.state.accountName,
				minimumStrength: action.payload.state.requestedPasswordMinimumStrength ?
					action.payload.state.requestedPasswordMinimumStrength.strength :
					0,
				strength,
				submitDisabled,
				violations
			};
		case getType(accountRecoveryScenePasswordInputChanged):
			return {...state, violations: {}};
		case getType(accountRecoveryScenePasswordStrengthEvaluated):
			return {...state, strength: action.payload.passwordStrength.strength};
		case getType(accountRecoveryScenePasswordSubmitted):
			return {...state, submitDisabled: true};
		case getType(accountRecoverySceneEnterPasswordValidationException):
			return {...state, submitDisabled: false, violations: action.payload.violations};
		default:
			return state;
	}
};

const indexReducer = (
	state: IndexState = indexInitialState,
	action: ActionTypes
): IndexState => {

	let remainingSeconds: number | undefined;
	let phoneVerificationActive: boolean | number | undefined;

	switch (action.type) {
		case getType(accountRecoverySceneActivated):
			return indexInitialState;
		case getType(accountRecoverySceneStateRetrieved):
		case getType(accountRecoverySceneStateChanged):
			if (action.payload.state.emailVerificationStopped || action.payload.state.recoveryExpired || action.payload.state.recoveryAborted)
				return {
					...state,
					displayType: DisplayType.DISPLAY_TYPE_NONE,
					message: 'De link om je wachtwood opnieuw in te stellen, is inmiddels verlopen',
					messageType: MessageType.ERROR
				};

			if (action.payload.state.phoneBlocked)
				return {
					...state,
					displayType: DisplayType.DISPLAY_TYPE_NONE,
					message: 'Uw telefoon is geblokkeerd, neem contact met ons op',
					messageType: MessageType.ERROR
				};

			if (action.payload.state.failedToSendPhoneVerificationCode)
				return {
					...state,
					displayType: DisplayType.DISPLAY_TYPE_RETRY_PHONE_VERIFICATION_START,
					message: 'Er is een technisch probleem met het verificatie proces. ' +
						'Wij kunnen op dit moment geen code sturen naar uw telefoon',
					messageType: MessageType.ERROR
				};

			if (action.payload.state.phoneBusy)
				return {
					...state,
					displayType: DisplayType.DISPLAY_TYPE_RETRY_PHONE_VERIFICATION_START,
					message: 'Uw telefoon werd gebruikt voor een verificatie in een ander scherm',
					messageType: MessageType.ERROR
				};

			if (action.payload.state.phoneUnblocked)
				return {
					...state,
					displayType: DisplayType.DISPLAY_TYPE_PHONE_VERIFICATION_CODE,
					message: 'Voordat het wachtwoord opnieuw ingesteld kan worden, ' +
						'willen we graag het telefoonnummer verifiëren',
					messageType: MessageType.INFO
				};

			if (action.payload.state.phoneVerificationStoppedWithReason)
				return {
					...state,
					displayType: DisplayType.DISPLAY_TYPE_PHONE_VERIFICATION_CODE,
					message: '',
					messageType: MessageType.INFO,
					phoneVerificationWasStopped: true
				};

			remainingSeconds = action.payload.state.phoneVerificationTimeRemainingInSeconds;
			phoneVerificationActive = remainingSeconds && Number.isInteger(remainingSeconds) && remainingSeconds > 0;

			if (phoneVerificationActive) {
				let messageType = MessageType.INFO;
				let message = 'We hebben een verificatiecode verzonden naar uw telefoon';
				if (state.phoneVerificationWasStopped)
					message = 'We hebben een nieuwe code verzonden naar uw telefoon. De vorige code is niet meer geldig';

				if (action.payload.state.failedToProcessPhoneVerificationCode) {
					message = 'Er is een technische fout opgetreden tijdens het verifiëren van uw code. ' +
						'De code is nog steeds geldig. U kunt het opnieuw proberen';
					messageType = MessageType.ERROR;
				}

				return {
					...state,
					displayType: DisplayType.DISPLAY_TYPE_PHONE_VERIFICATION_CODE,
					message,
					messageType
				};
			}

			if (action.payload.state.requestedPasswordMinimumStrength)
				return {
					...state,
					displayType: DisplayType.DISPLAY_TYPE_ENTER_PASSWORD,
					message: 'Kies een nieuw wachtwoord',
					messageType: MessageType.INFO
				};

			if (action.payload.state.recoveryComplete)
				return {
					...state,
					displayType: DisplayType.DISPLAY_TYPE_COMPLETION_MESSAGE,
					message: '',
					messageType: MessageType.INFO
				};

			return state;
		default:
			return state;
	}
};

const phoneVerificationCodeReducer = (
	state: PhoneVerificationCodeState = phoneVerificationCodeInitialState,
	action: ActionTypes
): PhoneVerificationCodeState => {
	let verificationStopped: boolean;
	let remainingSeconds: number | undefined;
	let phoneVerificationActive: number | boolean | undefined;

	// noinspection DuplicatedCode
	switch (action.type) {
		case getType(accountRecoverySceneActivated):
			return phoneVerificationCodeInitialState;
		case getType(accountRecoverySceneStateRetrieved):
		case getType(accountRecoverySceneStateChanged):
			verificationStopped = !!action.payload.state.phoneVerificationStoppedWithReason;

			if (verificationStopped || action.payload.state.phoneUnblocked) {
				const violations = {...state.violations};
				let violationMessage;

				switch (action.payload.state.phoneVerificationStoppedWithReason) {
					case 'INVALID_VERIFICATION_CODE':
						violationMessage = 'De ingevoerde verificatiecode was niet geldig';
						break;
					case 'VERIFICATION_TIMEOUT':
						violationMessage = 'Uw verificatiecode is verlopen';
						break;
					default:
						violationMessage = null;
				}

				if (violationMessage)
					violations.verificationCode = [{message: violationMessage}];

				return {
					...state,
					maximumDurationInSeconds: null,
					submitDisabled: action.payload.state.expectingPhoneVerificationRestart,
					verificationStopped: true,
					violations
				};
			}

			remainingSeconds = action.payload.state.phoneVerificationTimeRemainingInSeconds;
			phoneVerificationActive = remainingSeconds && Number.isInteger(remainingSeconds) && remainingSeconds > 0;
			if (phoneVerificationActive)
				return {
					...state,
					maximumDurationInSeconds: remainingSeconds || null,
					submitDisabled: false,
					verificationStopped: false,
					violations: {}
				};

			return state;
		case getType(accountRecoverySceneRequestRetryPhoneVerification):
		case getType(accountRecoverySceneRequestRestartPhoneVerification):
		case getType(accountRecoveryScenePhoneVerificationCodeSubmitted):
			return {...state, submitDisabled: true};
		case getType(accountRecoverySceneRetryPhoneVerificationUnexpectedError):
		case getType(accountRecoverySceneRestartPhoneVerificationUnexpectedError):
		case getType(accountRecoverySceneEnterPhoneVerificationCodeUnexpectedError):
			return {...state, submitDisabled: false};
		case getType(accountRecoverySceneEnterPhoneVerificationCodeValidationException):
			return {...state, violations: action.payload.violations, submitDisabled: false};
		case getType(accountRecoveryScenePhoneVerificationCodeInputChanged):
			return {...state, violations: {}};
		default:
			return state;
	}
};

// noinspection DuplicatedCode
const retryPhoneVerificationStartReducer = (
	state: RetryPhoneVerificationStartState = retryPhoneVerificationStartInitialState,
	action: ActionTypes
): RetryPhoneVerificationStartState => {
	switch (action.type) {
		case getType(accountRecoverySceneActivated):
			return retryPhoneVerificationStartInitialState;
		case getType(accountRecoverySceneStateRetrieved):
		case getType(accountRecoverySceneStateChanged):
		case getType(accountRecoverySceneRetryPhoneVerificationUnexpectedError):
			return {...state, submitDisabled: false};
		case getType(accountRecoverySceneRequestRetryPhoneVerification):
			return {...state, submitDisabled: true};
		default:
			return state;
	}
};

export default combineReducers({
	enterPassword: enterPasswordReducer,
	index: indexReducer,
	phoneVerificationCode: phoneVerificationCodeReducer,
	retryPhoneVerificationStart: retryPhoneVerificationStartReducer
});
